import { cn } from "@/common/helpers/utils";
import { getVehicleVinValidation, VEHICLE_VIN_LENGTH } from "@/common/helpers/vehicle-vin";
import { CheckIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

interface VehicleVinFieldStatusProps {
  value: string;
  /**
   * Whether the "must be 17 characters" length error may be shown. The invalid
   * character error is always shown live while typing; the length error is only
   * surfaced once the field is considered done (blurred or the form submitted).
   */
  showLengthError?: boolean;
  className?: string;
}

/**
 * Inline feedback for a VIN input shown beneath the field. It renders the
 * invalid-character error live, the length error once the field is done, a muted
 * character counter while the VIN is being typed, and a positive confirmation
 * once it is well-formed. Both errors may appear at the same time.
 */
export function VehicleVinFieldStatus({ value, showLengthError = false, className }: VehicleVinFieldStatusProps) {
  const { t } = useTranslation("masterdata");
  const { length, hasCharacterError, hasLengthError, isValid } = getVehicleVinValidation(value, showLengthError);

  const showProgress = length > 0 && length < VEHICLE_VIN_LENGTH && !hasLengthError;

  if (!hasCharacterError && !hasLengthError && !showProgress && !isValid) {
    return null;
  }

  return (
    <div className={cn("mt-1 space-y-0.5", className)}>
      {hasCharacterError ? <p className="text-error text-sm">{t("masterdata:VehicleVinInvalidCharacters")}</p> : null}
      {hasLengthError ? (
        <p className="text-error text-sm">{t("masterdata:VehicleVinMustBe17CharactersWithProgress", { length })}</p>
      ) : null}
      {showProgress ? (
        <p className="text-muted-foreground text-sm">
          {t("masterdata:VehicleVinProgress", { length, total: VEHICLE_VIN_LENGTH })}
        </p>
      ) : null}
      {isValid ? (
        <p className="text-success flex items-center gap-1 text-sm">
          <CheckIcon className="size-4 shrink-0" aria-hidden="true" />
          {t("masterdata:VehicleVinValid")}
        </p>
      ) : null}
    </div>
  );
}
